// components/dashboard/CheckinBereinigung.jsx
//
// Nach dem Check-in: Nichterschienene aus den vorläufigen Pools und Listen
// nehmen, Pools neu auslosen (27.09.2026).
//
// Ablauf beim Veranstalter: Pools werden VOR dem Turnier erzeugt (Listen für
// Kampfrichter und Orga). Am Turniertag checken die Sportler ein. Ist der
// Check-in zu, räumt dieser Knopf auf — erst Vorschau, dann ausführen.
// Server: GET/POST /api/brackets/turnier/:id/checkin-bereinigung

import React, { useState } from 'react';

const kopf = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

export default function CheckinBereinigung({ turnierId, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [vorschau, setVorschau] = useState(null);
  const [ergebnis, setErgebnis] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');

  const pruefen = async () => {
    setLaeuft(true); setFehler(''); setErgebnis(null);
    try {
      const r = await fetch(`/api/brackets/turnier/${turnierId}/checkin-bereinigung`, { headers: kopf() });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`);
      setVorschau(d);
    } catch (e) { setFehler(e.message); }
    finally { setLaeuft(false); }
  };

  const ausfuehren = async () => {
    const n = vorschau?.checkin?.fehlen || 0;
    if (!window.confirm(`${n} nicht erschienene Sportler aus Pools und Listen nehmen, Nachgemeldete aufnehmen und die betroffenen Pools neu auslosen?\n\n`
      + 'Laufende Pools bleiben unberührt. Anmeldungen und Startgebühren bleiben bestehen.')) return;
    setLaeuft(true); setFehler('');
    try {
      const r = await fetch(`/api/brackets/turnier/${turnierId}/checkin-bereinigung`, {
        method: 'POST', headers: kopf(), body: JSON.stringify({ bestaetigt: true }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`);
      // Kategorien, die erst durch Nachmeldungen zwei Anwesende haben, bekommen
      // jetzt ihren Pool — bestehende bleiben unberührt (nur_fehlende).
      const r2 = await fetch(`/api/brackets/turnier/${turnierId}/auto-generate`, {
        method: 'POST', headers: kopf(), body: JSON.stringify({ nur_fehlende: true }),
      });
      const d2 = await r2.json().catch(() => ({}));
      const neuePools = r2.ok && d2.success ? (d2.data?.brackets || d2.data?.erstellte_brackets || []) : [];
      setErgebnis({ ...d, neue_pools: Array.isArray(neuePools) ? neuePools : [], neue_pools_anzahl: d2.data?.brackets_erstellt ?? null });
      setVorschau(null);
      onFertig && onFertig();
    } catch (e) { setFehler(e.message); }
    finally { setLaeuft(false); }
  };

  const nichtsZuTun = vorschau && !vorschau.pools.length && !vorschau.formen.length && !vorschau.bruchtest.length;

  return (
    <div className="ci-bereinigung">
      <button className="ci-bereinigung-kopf" onClick={() => setOffen(!offen)} aria-expanded={offen}>
        <span>🧹 Check-in abgeschlossen oder nachgemeldet? <strong>Pools abgleichen</strong></span>
        <span>{offen ? '▾' : '▸'}</span>
      </button>
      {offen && (
        <div className="ci-bereinigung-inhalt">
          <p className="ci-sub">
            Wer nicht eingecheckt ist, kommt aus den vorläufigen Pools, Formen- und Bruchtest-Listen. Anwesende
            Nachgemeldete (z. B. per Handy am Turniertag) kommen hinein, auch in neue Pools. Betroffene Pools
            werden neu ausgelost. Bleibt in einem Pool nur einer übrig, wird er aufgelöst und aus dem Zeitplan genommen, und
            die Person erscheint bei den <strong>Einzelstartern</strong>. Pools mit laufenden Kämpfen bleiben unberührt.
          </p>
          <button className="ci-btn" onClick={pruefen} disabled={laeuft}>{laeuft && !vorschau ? 'prüft…' : '👁 Vorschau'}</button>
          {fehler && <div className="ci-fehler" role="alert">{fehler}</div>}

          {vorschau && (
            <div className="ci-bereinigung-vorschau">
              <p><strong>{vorschau.checkin.eingecheckt}</strong> von {vorschau.checkin.sportler} Sportlern eingecheckt,
                {' '}<strong>{vorschau.checkin.fehlen}</strong> fehlen.</p>
              {vorschau.warnung && <div className="ci-fehler">⚠️ {vorschau.warnung}</div>}
              {nichtsZuTun && <p>✅ Bestehende Pools und Listen stimmen. Fehlen Pools für Nachmeldungen, legt „Abgleichen" sie an.</p>}
              {(
                <>
                  {vorschau.pools.length > 0 && (
                    <ul>
                      {vorschau.pools.map((p) => (
                        <li key={p.bracket_id}>
                          <strong>{p.division_code}</strong>: {p.vorher} → {p.bleiben}
                          {p.rein?.length > 0 && <span className="ci-sub"> — dazu: {p.rein.map((r) => r.name).join(', ')}</span>}
                          {p.gestartet ? ' · ⚠️ läuft schon, bleibt unverändert'
                            : p.bleiben < 2 ? ' · wird aufgelöst (Einzelstarter)' : ' · wird neu ausgelost'}
                          {p.raus.length > 0 && <span className="ci-sub"> — raus: {p.raus.map((r) => r.name).join(', ')}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {vorschau.formen.length > 0 && (
                    <p className="ci-sub">Formen: {vorschau.formen.map((f) => `${f.division_code} (${[f.raus.length && `−${f.raus.length}`, f.rein?.length && `+${f.rein.length}`].filter(Boolean).join(' ')})`).join(', ')}</p>
                  )}
                  {vorschau.bruchtest.length > 0 && <p className="ci-sub">Bruchtest: {vorschau.bruchtest.length} raus</p>}
                  <button className="ci-btn ci-btn--haupt" onClick={ausfuehren} disabled={laeuft || !!vorschau.warnung}>
                    {laeuft ? 'gleicht ab…' : 'Jetzt abgleichen'}
                  </button>
                </>
              )}
            </div>
          )}

          {ergebnis && (
            <div className="ci-bereinigung-vorschau">
              <p>✅ Erledigt: {ergebnis.neu_ausgelost.length} Pools neu ausgelost, {ergebnis.aufgeloest.length} aufgelöst,
                {' '}{ergebnis.formen} aus Formen-Listen, {ergebnis.bruchtest} aus Bruchtest.</p>
              {ergebnis.unberuehrt.length > 0 && (
                <p>⚠️ Unverändert, weil schon begonnen: {ergebnis.unberuehrt.map((u) => u.division_code).join(', ')} —
                  bitte von Hand prüfen.</p>
              )}
              {ergebnis.aufgeloest.length > 0 && (
                <p>👉 Aufgelöste Pools: die Übriggebliebenen stehen jetzt unter <strong>Einzelstarter</strong> mit Vorschlägen.</p>
              )}
              {ergebnis.neue_pools_anzahl > 0 && (
                <p>🆕 {ergebnis.neue_pools_anzahl} neue Pools durch Nachmeldungen — sie haben noch keine Zeit und Matte: im <strong>Zeitplan</strong> einplanen.</p>
              )}
              {ergebnis.aus_zeitplan?.length > 0 && (
                <p>🗓 Aus dem Zeitplan genommen (kein Kampf mehr): {ergebnis.aus_zeitplan.join(', ')}. Die übrigen Zeiten bleiben stehen.</p>
              )}
              <p className="ci-sub">Neu ausgelost heißt: Startlisten und Pool-Ausdrucke neu drucken.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
